import type { GenericSelectData } from "~/components/select/SelectGeneric";

import { isoWeeksInYear } from "~/helpers/dateCalc";
import i18n from "~/i18n/i18n";

export interface IsoWeekPoint {
	week: number;
	year: number;
}

export interface NeededWeekRange {
	from: IsoWeekPoint | null;
	to: IsoWeekPoint | null;
}

interface ResolveNeededWeekRangeArgs {
	fromParam: string | null;
	now?: Date;
	toParam: string | null;
}

export const CLEARED_WEEK_PARAM = "";

export const DEFAULT_NEEDED_WEEK_SPAN = 7;

const START_WEEK_RANGE_CAP = 53;

const DAY_MS = 86_400_000;

const mondayBasedDay = (date: Date) => (date.getUTCDay() + 6) % 7;

// Date.UTC would remap a two-digit isoYear into the 1900s (50 -> 1950), so anchor Jan 4 without it.
const week1Monday = (isoYear: number): Date => {
	const jan4 = new Date(0);
	jan4.setUTCFullYear(isoYear, 0, 4);
	jan4.setUTCHours(0, 0, 0, 0);
	const monday = new Date(jan4);
	monday.setUTCDate(jan4.getUTCDate() - mondayBasedDay(jan4));
	return monday;
};

export const isoWeekOf = (date: Date): IsoWeekPoint => {
	const thursday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
	thursday.setUTCDate(thursday.getUTCDate() - mondayBasedDay(thursday) + 3);
	const year = thursday.getUTCFullYear();
	const week = 1 + Math.round((thursday.getTime() - week1Monday(year).getTime()) / (7 * DAY_MS));
	return { week, year };
};

export const mondayOfIsoWeek = (year: number, week: number): Date => {
	const monday = week1Monday(year);
	monday.setUTCDate(monday.getUTCDate() + (week - 1) * 7);
	return monday;
};

export const ymdToUtcDate = (ymd: string): Date | null => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
	if (!match) return null;
	return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
};

export const utcDateToYmd = (date: Date): string => date.toISOString().slice(0, 10);

export const formatIsoWeekParam = (year: number, week: number): string => `${year}-${String(week).padStart(2, "0")}`;

export const formatReadyWeekParam = (date: Date): string => {
	const { week, year } = isoWeekOf(date);
	return formatIsoWeekParam(year, week);
};

export const weeksInIsoYear = (year: number): number => isoWeeksInYear(new Date(Date.UTC(year, 0, 4)));

export const parseStartWeekParam = (param: string): IsoWeekPoint | null => {
	const match = /^(\d{4})-(\d{1,2})$/.exec(param.trim());
	if (!match) return null;
	const year = Number(match[1]);
	const week = Number(match[2]);
	if (week < 1 || week > weeksInIsoYear(year)) return null;
	return { week, year };
};

export const addIsoWeeks = (point: IsoWeekPoint, delta: number): IsoWeekPoint => {
	const monday = mondayOfIsoWeek(point.year, point.week);
	monday.setUTCDate(monday.getUTCDate() + delta * 7);
	return isoWeekOf(monday);
};

const isoWeekPointOrder = (point: IsoWeekPoint): number => mondayOfIsoWeek(point.year, point.week).getTime();

export const isoWeekSpan = (from: IsoWeekPoint, to: IsoWeekPoint): number =>
	Math.round((isoWeekPointOrder(to) - isoWeekPointOrder(from)) / (7 * DAY_MS)) + 1;

export const isValidStartWeekRange = (from: IsoWeekPoint, to: IsoWeekPoint): boolean => {
	const span = isoWeekSpan(from, to);
	return span >= 1 && span <= START_WEEK_RANGE_CAP;
};

export const formatStartWeekOptionLabel = (point: IsoWeekPoint): string =>
	i18n.t("produce:inventory.hardgoods.list.weekOption", { week: point.week, year: point.year });

export const isoWeekPointToOption = (point: IsoWeekPoint): GenericSelectData => ({
	code: formatIsoWeekParam(point.year, point.week),
	name: formatStartWeekOptionLabel(point)
});

export const buildStartWeekFromOptions = (referenceDate: Date = new Date()): GenericSelectData[] => {
	const current = isoWeekOf(referenceDate);
	const options: GenericSelectData[] = [];
	for (let year = current.year; year <= current.year + 1; year++) {
		for (let week = year === current.year ? current.week : 1; week <= weeksInIsoYear(year); week++) {
			options.push(isoWeekPointToOption({ week, year }));
		}
	}
	return options;
};

export const buildStartWeekToOptions = (from: IsoWeekPoint): GenericSelectData[] =>
	Array.from({ length: START_WEEK_RANGE_CAP }, (_, delta) => isoWeekPointToOption(addIsoWeeks(from, delta)));

export const withFallbackOption = (options: GenericSelectData[], point: IsoWeekPoint | null): GenericSelectData[] => {
	if (!point) return options;
	const code = formatIsoWeekParam(point.year, point.week);
	if (options.some((option) => option.code === code)) return options;
	return [isoWeekPointToOption(point), ...options];
};

export const resolveNeededWeekRange = ({
	fromParam,
	now = new Date(),
	toParam
}: ResolveNeededWeekRangeArgs): NeededWeekRange => {
	const defaultFrom = isoWeekOf(now);
	const parsedFrom =
		fromParam === null
			? defaultFrom
			: fromParam === CLEARED_WEEK_PARAM
				? null
				: (parseStartWeekParam(fromParam) ?? defaultFrom);
	const from = parsedFrom && isoWeekPointOrder(parsedFrom) < isoWeekPointOrder(defaultFrom) ? defaultFrom : parsedFrom;
	if (!from) return { from: null, to: null };

	const defaultTo = addIsoWeeks(from, DEFAULT_NEEDED_WEEK_SPAN - 1);
	const to = toParam === null ? defaultTo : toParam === CLEARED_WEEK_PARAM ? null : parseStartWeekParam(toParam);
	if (!to || !isValidStartWeekRange(from, to)) return { from, to: null };

	return { from, to };
};

export const defaultNeededWeekRangeArgs = (now: Date = new Date()) => {
	const from = isoWeekOf(now);
	const to = addIsoWeeks(from, DEFAULT_NEEDED_WEEK_SPAN - 1);
	return {
		neededWeekFromIsoWeek: from.week,
		neededWeekFromIsoYear: from.year,
		neededWeekToIsoWeek: to.week,
		neededWeekToIsoYear: to.year
	};
};
